import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, Printer, AlertCircle } from 'lucide-react';
import { BillingInfo, PaymentRecord } from '../lib/billing-hooks';
/**
 * InvoicePage — public receipt viewer.
 *
 * URL: /invoice/:paymentId
 *
 * Renders a thermal-printer-friendly comprobante for a given payment.
 *
 * MULTI-TENANT SAFETY: every fetch is keyed by `payment.restaurant_id`
 * (which comes from the payment row itself, not from any URL param or
 * tenant context). The billing info, the restaurant logo, and the
 * mesa label are all loaded with `.eq('restaurant_id', pay.restaurant_id)`
 * or by following its FK ids. No cross-tenant data ever reaches this
 * page.
 */
export function InvoicePage() {
  const {
    paymentId
  } = useParams<{
    paymentId: string;
  }>();
  const [payment, setPayment] = useState<PaymentRecord | null>(null);
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [tableLabel, setTableLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!paymentId) return;
      try {
        // 1) Payment row — authoritative source for restaurant_id.
        //    SEGURIDAD: se lee con una función segura en la base, que
        //    devuelve únicamente el pago solicitado. Antes se consultaba
        //    la tabla `payments` directamente.
        const {
          data: payRows,
          error: payErr
        } = await supabase.rpc('get_payment_receipt', {
          p_payment_id: paymentId
        });
        if (payErr) throw payErr;
        const pay = Array.isArray(payRows) ? payRows[0] ?? null : payRows ?? null;
        if (!pay) throw new Error('Factura no encontrada');
        // 2) Billing info — scoped to this payment's restaurant_id
        const {
          data: billingRows,
          error: bErr
        } = await supabase.rpc('get_billing_info_for_receipt', {
          p_payment_id: paymentId
        });
        if (bErr) throw bErr;
        const billing = Array.isArray(billingRows) ? billingRows[0] ?? null : billingRows ?? null;
        if (!billing) throw new Error('Datos del comercio no disponibles');
        // 3) Restaurant logo — scoped to the same restaurant_id. Non-fatal
        //    on failure; we just won't show the logo block.
        let logo: string | null = null;
        try {
          const {
            data: rest
          } = await supabase.from('restaurants').select('logo_url').eq('id', pay.restaurant_id).maybeSingle();
          if (rest && (rest as any).logo_url) {
            logo = (rest as any).logo_url as string;
          }
        } catch {
          logo = null;
        }
        // 4) Mesa label — only when the payment is linked to a table.
        //    `tables` rows have their own restaurant_id, so resolving by
        //    primary key here is tenant-safe by construction.
        let tLabel: string | null = null;
        if (pay.table_id) {
          const {
            data: tbl
          } = await supabase.from('tables').select('table_number, table_name').eq('id', pay.table_id).maybeSingle();
          if (tbl) tLabel = tbl.table_name || `Mesa ${tbl.table_number}`;
        }
        if (mounted) {
          setPayment(pay as PaymentRecord);
          setInfo(billing as BillingInfo);
          setLogoUrl(logo);
          setTableLabel(tLabel);
        }
      } catch (err: any) {
        if (mounted) setError(err.message || 'Error al cargar la factura');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [paymentId]);
  const fmt = (n: number) => new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    minimumFractionDigits: 0
  }).format(n);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-7469" data-mp="2r5ai3euaswpt">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" data-id="element-7470" data-mp="2m6qs93cl30s6" />
      </div>;
  }
  if (error || !payment || !info) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6" data-id="element-7471" data-mp="2h6kynhmr747g">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-lg" data-id="element-7472" data-mp="2c8t5kkadhc1o">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" data-id="element-7473" data-mp="27i30z99rtd81" />
          <h1 className="text-xl font-black text-gray-900 mb-1" data-id="element-7474" data-mp="27g01lymqa7qn">
            Factura no disponible
          </h1>
          <p className="text-sm text-gray-500" data-id="element-7475" data-mp="1yh4xcs38v7pz">
            {error || 'No se pudo cargar el documento.'}
          </p>
        </div>
      </div>;
  }
  // ─── Derived display values ───────────────────────────────────────
  const date = new Date(payment.created_at).toLocaleString('es-CR', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
  const consecStr = String(payment.consecutive_number).padStart(10, '0');
  const methodLabel = payment.method === 'cash' ? 'Efectivo' : payment.method === 'sinpe' ? 'SINPE Móvil' : 'Tarjeta';
  const docTitle = info.regime === 'simplificado' ? 'COMPROBANTE DE PAGO' : 'FACTURA ELECTRÓNICA';
  const regimeLabel = info.regime === 'simplificado' ? 'Régimen Simplificado' : info.regime === 'tradicional' ? 'Régimen Tradicional' : info.regime;
  return <div className="min-h-screen bg-gray-100 py-6 px-4 print:bg-white print:p-0" data-id="element-7476" data-mp="10lj1njjtqybv">
      <style data-id="element-7477" data-mp="15l5gfxfawmms">{`
        @media print {
          @page { margin: 6mm; size: 80mm auto; }
          body { background: white !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="max-w-sm mx-auto" data-id="element-7478" data-mp="cvyhixbxpv10">
        {/* Action bar (hidden when printing) */}
        <div className="no-print flex gap-2 mb-4" data-id="element-7479" data-mp="hvb4di2t323l">
          <button onClick={() => window.print()} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-900 text-white font-bold text-sm shadow-lg hover:bg-gray-800 transition-colors" data-id="element-7480" data-mp="9vxzizl7ibr7">
            <Printer className="w-4 h-4" data-id="element-7481" data-mp="jlhjy9okido6" />
            Imprimir / Guardar PDF
          </button>
        </div>

        <p className="no-print text-xs text-gray-500 text-center mb-4 leading-relaxed" data-id="element-7482" data-mp="ep9ancx1xeox">
          💡 En el diálogo de impresión, elige{' '}
          <strong data-id="element-7483" data-mp="3i4ep8b1m8owz">"Guardar como PDF"</strong> para descargar el comprobante, o
          seleccioná tu impresora térmica de 80&nbsp;mm.
        </p>

        {/* ───────── Receipt body ───────── */}
        <div className="bg-white p-5 shadow-md font-mono text-gray-900 border border-gray-200 print:border-0 print:shadow-none" data-id="element-7484" data-mp="3s1jeijqrd0i0">
          {/* Logo (when available) — matches the physical receipt: small,
           centered, square-ish, with a subtle border for printer
           contrast. Falls back gracefully when logo_url is missing. */}
          {logoUrl && <div className="flex justify-center mb-3" data-id="element-7485" data-mp="1exph00pd5ux4">
              <div className="w-20 h-20 rounded-md overflow-hidden border border-gray-200 bg-white flex items-center justify-center" data-id="element-7486" data-mp="1jv8wt51pmfr6">
                <img src={logoUrl} alt={info.business_name} className="w-full h-full object-contain"
            // Hide the image element if it fails to load — the
            // surrounding box still adds visual weight to the header.
            onError={e => {
              ;
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }} data-id="element-7487" data-mp="15wr9h8fpv59s" />
              </div>
            </div>}

          {/* Comercial / brand name (large, centered) */}
          <h1 className="text-base font-black text-center tracking-wide mb-0.5" data-id="element-7488" data-mp="2gjf7o9p864dw">
            {info.business_name}
          </h1>

          {/* Legal owner name (only for personas físicas with legal_name
           configured — e.g. Régimen Simplificado). Backwards compatible:
           when null/empty, this entire line is skipped. */}
          {info.legal_name && <p className="text-[11px] text-center font-bold uppercase tracking-wider mb-2" data-id="element-7489" data-mp="1mvr7mhj6gext">
              {info.legal_name}
            </p>}

          {/* Contact / legal block — compact, centered, mirrors the
           physical receipt's "address + phone + email" layout. */}
          <div className="text-[11px] text-center leading-snug mb-3 space-y-0.5" data-id="element-7490" data-mp="2alljavrrfxgh">
            <div data-id="element-7491" data-mp="25kxrw8zmdelq">
              <strong data-id="element-7492" data-mp="25kfpsvqa065w">
                Céd. {info.legal_id_type === 'fisica' ? '' : 'Jur. '}
                {info.legal_id}
              </strong>
            </div>
            {info.phone && <div data-id="element-7493" data-mp="u6trajyj0s7p">Tel. {info.phone}</div>}
            {info.address && <div data-id="element-7494" data-mp="u47jcj7q4aoy">{info.address}</div>}
            {info.email && <div className="break-all" data-id="element-7495" data-mp="p6xt51cifv02">{info.email}</div>}
          </div>

          {/* Document type + consecutive — visually anchored as a "stamp" */}
          <div className="border-y border-dashed border-gray-400 py-2 my-2" data-id="element-7496" data-mp="30spy9llq63nk">
            <p className="text-[11px] font-black tracking-wider text-center mb-0.5" data-id="element-7497" data-mp="35tx8drbcbexe">
              {docTitle}
            </p>
            <p className="text-center font-mono text-xs tabular-nums" data-id="element-7498" data-mp="3aqxgs8vnwtro">
              {consecStr}
              {payment.is_partial && <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[9px] font-bold" data-id="element-7499" data-mp="3kjbkv8a7qch0">
                  PARCIAL
                </span>}
            </p>
            <p className="text-[10px] text-center text-gray-600 mt-0.5" data-id="element-7500" data-mp="3tiyl3qr7hc8h">
              {date}
            </p>
            {tableLabel && <p className="text-[10px] text-center text-gray-700 mt-0.5" data-id="element-7501" data-mp="2c5kop48lh50">
                <strong data-id="element-7502" data-mp="1z68o305jjmwv">{tableLabel}</strong>
              </p>}
          </div>

          {/* Items table — column widths tuned for 80mm thermal paper */}
          <table className="w-full text-[11px] my-2" data-id="element-7503" data-mp="1hx7m0drxju5r">
            <thead data-id="element-7504" data-mp="1hysgy4i5pf45">
              <tr className="border-b border-gray-900 uppercase text-[10px]" data-id="element-7505" data-mp="1hzamxsumf7p9">
                <td className="pb-1 w-8" data-id="element-7506" data-mp="1mwaq9w035wco">Cant.</td>
                <td className="pb-1" data-id="element-7507" data-mp="1rum0w8y7gavy">Descripción</td>
                <td className="pb-1 text-right whitespace-nowrap" data-id="element-7508" data-mp="1rx88g8l3iqe6">
                  Monto total
                </td>
              </tr>
            </thead>
            <tbody data-id="element-7509" data-mp="20wlj34kyhl0h">
              {(payment.items_json || []).map((it, idx) => <tr key={idx} className="align-top" data-id="element-7510" data-mp="25uda7wds133m" data-mpi={idx}>
                  <td className="font-bold pt-1.5" data-id="element-7511" data-mp="25t29k5fmobgq">{it.quantity}</td>
                  <td className="pr-2 pt-1.5" data-id="element-7512" data-mp="2amoax1gccsx8">
                    <div className="leading-tight" data-id="element-7513" data-mp="2anzep19se0oc">{it.item_name}</div>
                    {it.notes && <div className="text-[10px] text-gray-500 italic mt-0.5" data-id="element-7514" data-mp="2jmbentwjmatg">
                        {it.notes}
                      </div>}
                  </td>
                  <td className="text-right tabular-nums whitespace-nowrap pt-1.5" data-id="element-7515" data-mp="2oj1seakvkubp">
                    {fmt(it.line_total)}
                  </td>
                </tr>)}
            </tbody>
          </table>

          {/* Total — bold and visually dominant, matching the physical
           receipt's "Total ₡4,400.00" style. */}
          <div className="border-t-2 border-gray-900 pt-2 mt-2" data-id="element-7516" data-mp="3v4fn28dq5ezo">
            <div className="flex justify-between items-baseline" data-id="element-7517" data-mp="voiyq54zrkif">
              <span className="text-lg font-black" data-id="element-7518" data-mp="vnr2qt7hfkrm">Total</span>
              <span className="text-lg font-black tabular-nums" data-id="element-7519" data-mp="rmp2qa5qr4ol">
                {fmt(payment.amount)}
              </span>
            </div>
          </div>

          {/* Payment metadata — Moneda + F. Pago */}
          <div className="border-t border-dashed border-gray-400 pt-2 mt-3 space-y-0.5 text-[11px]" data-id="element-7520" data-mp="hw2u4zwfzefe">
            <div className="flex justify-between" data-id="element-7521" data-mp="1fdkxng5hug02">
              <span data-id="element-7522" data-mp="1alt7h6q92cag">Moneda</span>
              <strong data-id="element-7523" data-mp="1aljeda9wrp0j">CRC</strong>
            </div>
            <div className="flex justify-between" data-id="element-7524" data-mp="1ajqb84yi7mxa">
              <span data-id="element-7525" data-mp="1tj35c5ak96vt">F. Pago</span>
              <strong data-id="element-7526" data-mp="1titnj4zo6617">{methodLabel}</strong>
            </div>
          </div>

          {/* Regime + footer note + legal disclaimer */}
          <p className="text-[11px] mt-3 font-medium" data-id="element-7527" data-mp="1kpzum4rav84v">{regimeLabel}</p>
          {info.footer_note && <p className="text-center text-[12px] mt-4 mb-1 font-medium text-gray-800" data-id="element-7528" data-mp="1or384klvks2x">
              {info.footer_note}
            </p>}
          <p className="text-center text-[9px] mt-3 text-gray-500 leading-tight" data-id="element-7529" data-mp="1apzbaitfks0o">
            {info.regime === 'simplificado' ? 'Documento equivalente — no constituye factura electrónica.' : 'Documento autorizado por la DGT — Costa Rica.'}
          </p>
          <p className="text-center text-[9px] mt-1 text-gray-400" data-id="element-7530" data-mp="15t8tnr1z9od5">
            Powered by NOIZA
          </p>
        </div>
      </div>
    </div>;
}